// Pick the canvas color mode an imported/re-opened model should open with (spec:
// import color-mode selection). Desktop stores its active "color by" in
// gradient_colors.criteria; Connect's dropdown (resultsDisplayMode) is a separate
// runtime coloring that import otherwise leaves on 'none' — so imported models open
// blank. This derives a RENDERABLE mode from the imported criterion:
//   - elevation / fluid_zone render with no calculation → use them directly.
//   - `messages` is PIPE-FLO's neutral/default "color by" (effectively uncolored) → 'none'.
//   - every other criterion (velocity, fluid_temperature, hl_per_100, …) needs solver
//     or thermal results a fresh import lacks → fall back to fluid_zone when the model
//     has zones to color by, else 'none'.
//   - no gradient_colors (new/blank model) → 'none' (unchanged behavior).
// gradient_colors persists in the system JSON, so deriving here also keeps the mode
// on re-open (openModel), not just first import.

import type { ResultsDisplayMode } from './model-store'

// `| undefined` is required on the optionals under exactOptionalPropertyTypes so
// `HydraulicSystem` (gradient_colors: GradientColors | undefined) assigns without a cast.
export interface DisplayModeInput {
  readonly gradient_colors?: { readonly criteria?: string } | undefined
  readonly fluid_zones?: ReadonlyArray<unknown> | undefined
}

/** Desktop criteria that map 1:1 to a Connect mode renderable with no calculation. */
const NO_CALC_MODE: Record<string, ResultsDisplayMode> = {
  elevation: 'elevation',
  fluid_zone: 'fluid_zone',
}

export function initialDisplayMode(system: DisplayModeInput | null | undefined): ResultsDisplayMode {
  const criteria = system?.gradient_colors?.criteria
  if (criteria === undefined) return 'none'

  // `messages` is PIPE-FLO's neutral/default "color by" — the model was effectively
  // uncolored in Desktop, so don't invent fluid-zone coloring for it.
  if (criteria === 'messages') return 'none'

  const direct = NO_CALC_MODE[criteria]
  if (direct) return direct

  // Criterion needs results the import doesn't have yet; fluid-zone coloring is the
  // no-calc proxy, but only if there are zones to color by.
  return (system?.fluid_zones?.length ?? 0) > 0 ? 'fluid_zone' : 'none'
}
